import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, Camera, CameraOff, RefreshCw, Webcam, CheckCircle2, Play, Send, Monitor } from "lucide-react";

const GRACE_SECONDS = 3;
const SNAPSHOT_MS = 60000;
const MAX_VIOLATIONS = 3;

export default function TestRunner() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("camera"); // camera -> test
  const [camState, setCamState] = useState("idle"); // idle | granted | denied
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(null);
  const [warning, setWarning] = useState(false);
  const [warningType, setWarningType] = useState(null);
  const [graceLeft, setGraceLeft] = useState(GRACE_SECONDS);
  const [busy, setBusy] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const sessionRef = useRef(null);
  const answersRef = useRef({});
  const violationsRef = useRef(0);
  const terminatedRef = useRef(false);
  const warningRef = useRef(false);
  const graceTimerRef = useRef(null);
  const fsDebounceRef = useRef(null);
  const submittedRef = useRef(false);
  const armedRef = useRef(false);
  const armTimerRef = useRef(null);

  const logEvent = useCallback((event, detail = "") => {
    const s = sessionRef.current;
    if (s) api.post(`/tests/${s.id}/event`, { event, detail }).catch(() => {});
  }, []);

  const terminate = useCallback(async (reason) => {
    if (terminatedRef.current) return;
    terminatedRef.current = true;
    const s = sessionRef.current;
    if (s) await api.post(`/tests/${s.id}/terminate`, { reason }).catch(() => {});
    streamRef.current?.getTracks().forEach((t) => t.stop());
    navigate("/test/completamento");
  }, [navigate]);

  const enterFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) {
      el.requestFullscreen().then(() => logEvent("FULLSCREEN_ENTER")).catch(() => logEvent("FULLSCREEN_ENTER_FAILED"));
    }
  }, [logEvent]);

  const captureSnapshot = useCallback(() => {
    const video = videoRef.current;
    const s = sessionRef.current;
    if (!video || !s || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = Math.round(640 * (video.videoHeight / video.videoWidth)) || 480;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = canvas.toDataURL("image/jpeg", 0.6);
    api.post(`/tests/${s.id}/snapshot`, { image }).catch(() => {});
  }, []);

  const submitSectionA = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const s = sessionRef.current;
    if (!s) return;
    try {
      const { data } = await api.post(`/tests/${s.id}/submit-section-a`);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (data.status === "section_b_active") {
        navigate("/test/parte-b");
      } else {
        navigate("/test/completamento");
      }
    } catch {
      submittedRef.current = false;
      setBusy(false);
    }
  }, [navigate]);

  useEffect(() => {
    const sid = localStorage.getItem("its_test_session");
    if (!sid) {
      navigate("/dashboard");
      return;
    }
    const load = async () => {
      try {
        const { data } = await api.get(`/tests/${sid}`);
        if (data.status === "section_b_active") { navigate("/test/parte-b"); return; }
        if (["completed", "screened_out", "terminated"].includes(data.status)) { navigate("/test/completamento"); return; }
        sessionRef.current = data;
        setSession(data);
        setAnswers(data.answers || {});
        answersRef.current = data.answers || {};
      } catch {
        navigate("/dashboard");
      }
    };
    load();
  }, [navigate]);

  // Elenca le videocamere disponibili (i nomi completi appaiono dopo il consenso)
  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const vids = all.filter((d) => d.kind === "videoinput");
      setDevices(vids.map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Videocamera ${i + 1}` })));
      setSelectedDevice((cur) => cur || (vids[0]?.deviceId ?? ""));
    } catch (e) {
      console.error("Elenco videocamere non disponibile", e);
    }
  }, []);

  useEffect(() => {
    if (session && phase === "camera") refreshDevices();
  }, [session, phase, refreshDevices]);

  const changeDevice = (deviceId) => {
    setSelectedDevice(deviceId);
    if (camState === "granted") {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setCamState("idle");
    }
  };

  // Richiesta esplicita del permesso webcam: il test non parte senza
  const enableCamera = useCallback(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamState("denied");
      logEvent("CAMERA_PERMISSION_DENIED", "API non disponibile");
      return;
    }
    const constraints = { video: selectedDevice ? { deviceId: { exact: selectedDevice } } : true };
    navigator.mediaDevices.getUserMedia(constraints)
      .then((stream) => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCamState("granted");
        logEvent("CAMERA_PERMISSION_GRANTED");
        refreshDevices();
      })
      .catch(() => {
        setCamState("denied");
        logEvent("CAMERA_PERMISSION_DENIED");
      });
  }, [logEvent, selectedDevice, refreshDevices]);

  const startTest = () => {
    enterFullscreen();
    setPhase("test");
  };

  // Collega lo stream webcam sia alla preview (fase camera) sia al PIP (fase test)
  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [camState, phase, session]);

  useEffect(() => {
    if (!session || phase !== "test") return;
    const snap = setInterval(captureSnapshot, SNAPSHOT_MS);
    return () => clearInterval(snap);
  }, [session, phase, captureSnapshot]);

  useEffect(() => {
    if (!session || phase !== "test") return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(session.closed_deadline) - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) submitSectionA();
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [session, phase, submitSectionA]);

  useEffect(() => {
    if (!session || phase !== "test") return;
    // Il monitoraggio si arma solo dopo che lo schermo intero è stabilmente attivo:
    // l'ingresso in fullscreen e le finestre del browser (es. permesso webcam) non devono
    // generare alert né violazioni. Fallback temporale se la richiesta fullscreen fallisce.
    armedRef.current = false;
    const arm = () => {
      if (!armedRef.current) {
        armedRef.current = true;
        logEvent("PROCTORING_ARMED");
      }
    };
    const scheduleArm = (ms) => {
      clearTimeout(armTimerRef.current);
      armTimerRef.current = setTimeout(arm, ms);
    };
    if (document.fullscreenElement) scheduleArm(1200);
    else armTimerRef.current = setTimeout(arm, 4000);
    const startViolation = (type, logName, maxReason, graceReason) => {
      if (!armedRef.current) return;
      if (warningRef.current || terminatedRef.current || submittedRef.current) return;
      warningRef.current = true;
      setWarningType(type);
      violationsRef.current += 1;
      logEvent(logName, `Violazione n. ${violationsRef.current}`);
      if (violationsRef.current >= MAX_VIOLATIONS) {
        terminate(maxReason);
        return;
      }
      setGraceLeft(GRACE_SECONDS);
      setWarning(true);
      graceTimerRef.current = setInterval(() => {
        setGraceLeft((g) => {
          if (g <= 1) {
            clearInterval(graceTimerRef.current);
            terminate(graceReason);
            return 0;
          }
          return g - 1;
        });
      }, 1000);
    };
    const clearWarning = (logName) => {
      warningRef.current = false;
      clearInterval(graceTimerRef.current);
      setWarning(false);
      setWarningType(null);
      logEvent(logName);
    };
    const onFsChange = () => {
      if (terminatedRef.current || submittedRef.current) return;
      clearTimeout(fsDebounceRef.current);
      if (!document.fullscreenElement) {
        // L'ingresso in schermo intero e le transizioni di rotta generano eventi transitori:
        // l'avviso parte solo se l'uscita persiste oltre una breve finestra
        fsDebounceRef.current = setTimeout(() => {
          if (terminatedRef.current || submittedRef.current || document.fullscreenElement) return;
          startViolation("fullscreen", "FULLSCREEN_EXIT_WARNING",
            "Superato il numero massimo di uscite dallo schermo intero",
            "Uscita dallo schermo intero oltre i 3 secondi consentiti");
        }, 500);
      } else {
        scheduleArm(1200);
        if (warningRef.current) clearWarning("FULLSCREEN_RESTORED");
      }
    };
    const onBlur = () => {
      if (terminatedRef.current || submittedRef.current) return;
      startViolation("blur", "WINDOW_BLUR_WARNING",
        "Superato il numero massimo di cambi finestra o applicazione",
        "Cambio finestra o applicazione oltre i 3 secondi consentiti");
    };
    const onFocus = () => {
      if (warningRef.current && document.fullscreenElement) clearWarning("WINDOW_FOCUS_RESTORED");
    };
    document.addEventListener("fullscreenchange", onFsChange);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      clearInterval(graceTimerRef.current);
      clearTimeout(fsDebounceRef.current);
      clearTimeout(armTimerRef.current);
    };
  }, [session, phase, logEvent, terminate]);

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="test-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-700 border-t-sky-400" />
      </div>
    );
  }

  // Schermata dedicata: permesso webcam obbligatorio prima dell'avvio del test
  if (phase === "camera") {
    return (
      <div className="min-h-screen bg-[#0B1120] text-slate-100 flex items-center justify-center p-6" data-testid="camera-permission-screen">
        <div className="max-w-lg w-full">
          <div className="text-center">
            <Webcam className="h-14 w-14 text-sky-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Attiva la webcam per iniziare</h1>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">
              Questa è una sessione d'esame proctored. Per avviare il test è <strong>obbligatorio</strong> consentire
              l'accesso alla webcam: il sistema acquisirà snapshot ogni 60 secondi a garanzia della regolarità della prova.
            </p>
          </div>
          <div className="mt-6 bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 text-left flex items-start gap-3" data-testid="camera-desktop-note">
            <Monitor className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-100/90 leading-relaxed">
              <strong className="text-amber-300">Consiglio:</strong> svolgi il test da computer per un'esperienza
              ottimale (webcam e schermo intero sono più affidabili). Se hai iniziato la candidatura da telefono,
              puoi riprendere da dove eri rimasto: accedi alla tua area personale da computer con le stesse credenziali.
            </p>
          </div>
          <div className="mt-6 bg-slate-800/80 border border-slate-700 rounded-xl p-5 text-left" data-testid="camera-durations-info">
            <p className="proctor-badge text-sky-400 mb-3">TEMPO A DISPOSIZIONE</p>
            <p className="text-sm text-slate-300 leading-relaxed">
              Avrai <strong className="text-slate-100">{session.duration_a_minutes ?? 30} minuti</strong> per la
              Sezione A (test tecnico) e <strong className="text-slate-100">{session.duration_b_minutes ?? 15} minuti</strong> per
              la Sezione B (la rotta ITS, mappa di allineamento al percorso). Allo scadere del tempo la sezione viene consegnata automaticamente.
            </p>
          </div>
          <div className="mt-6 bg-slate-800/80 border border-slate-700 rounded-xl p-5 text-left">
            <p className="proctor-badge text-sky-400 mb-3">COME FARE</p>
            <ol className="space-y-2 text-sm text-slate-300 list-decimal list-inside">
              <li>Clicca «Attiva webcam» qui sotto.</li>
              <li>Quando il browser lo chiede, seleziona <strong>«Consenti»</strong> per l'accesso alla webcam.</li>
              <li>Verifica di vederti correttamente nell'anteprima.</li>
              <li>Clicca «Avvia il test»: si aprirà lo schermo intero e partirà il timer.</li>
            </ol>
          </div>
          <div className="mt-6 text-left">
            <label htmlFor="camera-device-select" className="proctor-badge text-sky-400 block mb-2">SCEGLI LA VIDEOCAMERA</label>
            {devices.length > 0 ? (
              <select
                id="camera-device-select"
                data-testid="camera-device-select"
                value={selectedDevice}
                onChange={(e) => changeDevice(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-sky-400"
              >
                {devices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                ))}
              </select>
            ) : (
              <p data-testid="camera-no-devices" className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                Nessuna videocamera rilevata. Collega una webcam e ricarica la pagina: il test non può
                iniziare senza un dispositivo video attivo.
              </p>
            )}
          </div>
          <div className="mt-6 rounded-xl overflow-hidden border border-slate-700 bg-slate-800 min-h-40 flex items-center justify-center">
            {camState === "granted" ? (
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-56 object-cover" data-testid="camera-preview" />
            ) : camState === "denied" ? (
              <div className="p-6 text-center" data-testid="camera-blocked-screen">
                <CameraOff className="h-8 w-8 text-red-400 mx-auto mb-2" />
                <p className="text-sm text-red-300">
                  Accesso alla webcam negato. Controlla le autorizzazioni del browser per questo sito
                  (icona a sinistra dell'indirizzo) e riprova: il test non può iniziare senza webcam.
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500 p-6">L'anteprima della webcam apparirà qui.</p>
            )}
          </div>
          <div className="mt-6 flex justify-center gap-3">
            {camState !== "granted" ? (
              <Button data-testid="camera-enable-button" size="lg" className="bg-sky-500 hover:bg-sky-400 text-slate-900"
                disabled={devices.length === 0} onClick={enableCamera}>
                <Camera className="h-4 w-4 mr-2" /> {camState === "denied" ? "Riprova" : "Attiva webcam"}
              </Button>
            ) : (
              <Button data-testid="camera-start-test-button" size="lg" className="bg-[#E05A47] hover:bg-[#c94836]" onClick={startTest}>
                <Play className="h-4 w-4 mr-2" /> Avvia il test
              </Button>
            )}
            <Button data-testid="camera-abort-button" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700"
              onClick={() => { streamRef.current?.getTracks().forEach((t) => t.stop()); navigate("/dashboard"); }}>
              Esci
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const questions = session.questions || [];
  const q = questions[idx];
  const mm = String(Math.floor((remaining ?? 0) / 60)).padStart(2, "0");
  const ss = String((remaining ?? 0) % 60).padStart(2, "0");
  const answered = Object.keys(answers).length;

  const choose = (qid, optionIndex) => {
    setAnswers((prev) => {
      const next = { ...prev, [qid]: optionIndex };
      answersRef.current = next;
      return next;
    });
    api.post(`/tests/${session.id}/answer`, { question_id: qid, option_index: optionIndex }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100" data-testid="test-runner">
      <div className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div>
            <div className="proctor-badge text-sky-400">SEZIONE A · TEST A RISPOSTA CHIUSA</div>
            <div className="text-xs text-slate-400">Risposte salvate: {answered}/{questions.length}</div>
          </div>
          <div className="flex items-center gap-4">
            <div data-testid="proctor-timer-countdown" className={`font-mono2 text-xl font-semibold ${remaining !== null && remaining < 300 ? "text-red-400" : "text-slate-100"}`}>
              {mm}:{ss}
            </div>
            <Button data-testid="test-submit-button" size="sm" disabled={busy} className="bg-[#E05A47] hover:bg-[#c94836]"
              onClick={() => { setBusy(true); submitSectionA(); }}>
              {busy ? "Consegna..." : "Concludi sezione A"}
            </Button>
          </div>
        </div>
        <Progress value={questions.length ? (answered / questions.length) * 100 : 0} className="h-1 rounded-none" />
      </div>

      <div data-testid="proctor-webcam-pip" className="fixed top-20 right-4 z-40 w-32 sm:w-44 rounded-lg overflow-hidden border border-slate-600 bg-slate-800 shadow-2xl">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-900/95">
          <span className="rec-dot h-2 w-2 rounded-full bg-red-500" />
          <span className="proctor-badge text-red-400 text-[10px]">PROCTORING ATTIVO</span>
        </div>
        <video ref={videoRef} autoPlay muted playsInline className="w-full h-28 object-cover" />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-10 sm:py-10">
        {q && (
          <Card data-testid="test-question-card" className="bg-slate-800/80 border-slate-700 text-slate-100">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-4">
                <span className="proctor-badge text-sky-400">{q.category.toUpperCase()}</span>
                <span className="text-xs text-slate-400">Domanda {idx + 1} di {questions.length} · {q.points} {q.points === 1 ? "punto" : "punti"}</span>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold leading-relaxed text-slate-100">{q.text}</h2>
              <RadioGroup className="mt-6 space-y-3" value={answers[q.id] !== undefined ? String(answers[q.id]) : ""}
                onValueChange={(v) => choose(q.id, parseInt(v, 10))}>
                {q.options.map((opt, oi) => (
                  <label key={`${q.id}-${oi}`} data-testid={`test-option-${oi}`}
                    className={`flex items-center gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                      answers[q.id] === oi ? "border-sky-400 bg-sky-400/10" : "border-slate-600 hover:border-slate-500"
                    }`}>
                    <RadioGroupItem value={String(oi)} />
                    <span className="text-sm sm:text-base text-slate-100">{opt}</span>
                  </label>
                ))}
              </RadioGroup>
              <div className="mt-8 flex flex-wrap justify-between gap-3">
                <Button data-testid="test-prev-button" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700"
                  disabled={idx === 0} onClick={() => setIdx(idx - 1)}>
                  Precedente
                </Button>
                {idx < questions.length - 1 ? (
                  <Button data-testid="test-next-button" className="bg-sky-500 hover:bg-sky-400 text-slate-900"
                    onClick={() => setIdx(idx + 1)}>
                    Successiva
                  </Button>
                ) : answers[q.id] !== undefined ? (
                  <Button data-testid="section-a-final-submit-button" size="lg" disabled={busy}
                    className="w-full sm:w-auto bg-[#E05A47] hover:bg-[#c94836] font-semibold"
                    onClick={() => { setBusy(true); submitSectionA(); }}>
                    <Send className="h-4 w-4 mr-2" /> {busy ? "Invio in corso..." : "Invia risposte e concludi test"}
                  </Button>
                ) : (
                  <Button data-testid="test-next-button" className="bg-sky-500 hover:bg-sky-400 text-slate-900" disabled>
                    Successiva
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-6 flex flex-wrap gap-2" data-testid="question-navigator">
          {questions.map((qq, i) => (
            <button key={qq.id} data-testid={`question-nav-${i}`} onClick={() => setIdx(i)}
              className={`h-8 w-8 rounded text-xs font-mono2 font-semibold transition-colors ${
                i === idx ? "bg-sky-400 text-slate-900" : answers[qq.id] !== undefined ? "bg-slate-600 text-slate-100" : "bg-slate-800 text-slate-400 border border-slate-700"
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      {warning && (
        <div className="fixed inset-0 z-50 bg-red-950/95 backdrop-blur-md flex items-center justify-center p-6" data-testid="proctor-warning-overlay">
          <div className="max-w-md text-center">
            <AlertTriangle className="h-14 w-14 text-red-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-white">
              {warningType === "blur" ? "Hai lasciato la finestra del test" : "Sei uscito dallo schermo intero"}
            </h2>
            <p className="mt-3 text-red-200 text-sm leading-relaxed">
              {warningType === "blur"
                ? "Il passaggio a un'altra finestra, scheda o applicazione non è consentito durante la prova. Torna immediatamente alla finestra del test, altrimenti il test verrà annullato."
                : "Rientra immediatamente in modalità schermo intero, altrimenti il test verrà annullato."}
            </p>
            <div data-testid="proctor-grace-countdown" className="mt-6 font-mono2 text-6xl font-bold text-red-400">{graceLeft}</div>
            <Button data-testid="proctor-restore-fullscreen-btn" className="mt-6 bg-red-500 hover:bg-red-400" onClick={enterFullscreen}>
              <Camera className="h-4 w-4 mr-2" /> {warningType === "blur" ? "Riprendi il test" : "Rientra a schermo intero"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
